import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useTenantStore } from '@/store/tenantStore';
import { useAuthStore, clearRememberedWorkspace } from '@/store/authStore';
import { Button } from '@/components/ui/Button';

export default function SessionLimitPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slug: storeSlug, sessionLimit } = useTenantStore();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const slug = searchParams.get('slug') || storeSlug;
  const retryAfterMinutes = Number(searchParams.get('retryAfterMinutes') || '5');
  const limit = Number(searchParams.get('limit') || sessionLimit || 1);
  const message = searchParams.get('message') || `Maximum active sessions reached. Any idle sessions will clear automatically within ${retryAfterMinutes} minutes. Please try again shortly.`;
  const [secondsRemaining, setSecondsRemaining] = useState(Math.max(1, retryAfterMinutes) * 60);
  const countdownLabel = useMemo(() => {
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [secondsRemaining]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSecondsRemaining((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  function handleTryDifferentAccount() {
    clearRememberedWorkspace();
    navigate('/login');
  }

  function handleCancelSignOut() {
    clearAuth();
    navigate('/login');
  }

  function handleRetry() {
    navigate(slug ? `/login?slug=${encodeURIComponent(slug)}` : '/login');
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="wl-card p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100">
            <ShieldAlert className="h-7 w-7 text-yellow-600" />
          </span>
        </div>
        <h1 className="text-lg font-bold text-neutral-900 mb-2">Session Limit Reached</h1>
        <p className="text-sm text-neutral-600 mb-6">
          {message}
        </p>
        <p className="text-sm text-neutral-600 mb-6">
          Your plan allows <strong>{limit}</strong> active session
          {limit !== 1 ? 's' : ''}. You can retry when idle sessions clear.
        </p>
        <p className="text-2xl font-semibold text-neutral-900 mb-6" aria-live="polite">
          {countdownLabel}
        </p>
        <div className="flex flex-col gap-3">
          <Button variant="primary" className="w-full" onClick={handleRetry}>
            Retry Sign In
          </Button>
          <p className="text-sm text-neutral-500">
            If another device is actively using the shop account, ask the shop owner to revoke that device or upgrade the plan for more concurrent sessions.
          </p>
          <Button variant="ghost" className="w-full" onClick={handleTryDifferentAccount}>
            Try a Different Account
          </Button>
          <Button variant="danger" className="w-full" onClick={handleCancelSignOut}>
            Cancel and Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}
